import { useEffect, useState } from "react";
import axios from "axios";
import { ENV_REACT_APP } from "../Configurations/ENV_Config";

export interface PublicSiteConfig {
  ownerPhotoCap: number;
  premiumGatingEnabled: boolean;
  freeOwnerPhotoLimit: number;
  /**
   * Vendors bid on service requests. Off: requests go to the SimplerHome team,
   * who connect the homeowner with a pro themselves (Asher, October 2026).
   */
  vendorBiddingEnabled: boolean;
  /**
   * Whether paid checkout is open (BILLING_ENABLED). Off until launch, so the
   * /premium page shows "coming soon" rather than a Get Premium button.
   */
  billingEnabled: boolean;
}

// Matches the server defaults, so a failed fetch degrades to "everything
// unlocked" — the October behaviour — rather than locking anyone out.
export const SITE_CONFIG_DEFAULTS: PublicSiteConfig = {
  ownerPhotoCap: 5,
  premiumGatingEnabled: false,
  freeOwnerPhotoLimit: 1,
  vendorBiddingEnabled: false,
  billingEnabled: false,
};

const apiBase = (path: string) =>
  `${ENV_REACT_APP?.REACT_APP_API_BASE_URL || ""}${path}`;

// One request per page load, shared by every caller.
let cached: PublicSiteConfig | null = null;
let inflight: Promise<PublicSiteConfig> | null = null;

const load = (): Promise<PublicSiteConfig> => {
  if (cached) return Promise.resolve(cached);
  if (!inflight) {
    inflight = Promise.resolve()
      .then(() => axios.get(apiBase("/api/site-config/public")))
      .then((r) => {
        const d = r?.data?.data || {};
        cached = {
          ownerPhotoCap: Number.isInteger(d.ownerPhotoCap)
            ? d.ownerPhotoCap
            : SITE_CONFIG_DEFAULTS.ownerPhotoCap,
          premiumGatingEnabled: d.premiumGatingEnabled === true,
          freeOwnerPhotoLimit: Number.isInteger(d.freeOwnerPhotoLimit)
            ? d.freeOwnerPhotoLimit
            : SITE_CONFIG_DEFAULTS.freeOwnerPhotoLimit,
          vendorBiddingEnabled: d.vendorBiddingEnabled === true,
          billingEnabled: d.billingEnabled === true,
        };
        return cached;
      })
      .catch(() => {
        inflight = null; // let a later mount retry
        return SITE_CONFIG_DEFAULTS;
      });
  }
  return inflight;
};

/** Public runtime knobs (photo caps, free/premium gating, vendor bidding). */
export const useSiteConfig = (): { config: PublicSiteConfig; loaded: boolean } => {
  const [config, setConfig] = useState<PublicSiteConfig>(cached || SITE_CONFIG_DEFAULTS);
  const [loaded, setLoaded] = useState<boolean>(Boolean(cached));

  useEffect(() => {
    let alive = true;
    load().then((c) => {
      if (!alive) return;
      setConfig(c);
      setLoaded(true);
    });
    return () => {
      alive = false;
    };
  }, []);

  return { config, loaded };
};

/** Test hook — forget the cached config. */
export const __resetSiteConfigCache = () => {
  cached = null;
  inflight = null;
};

/** Test hook — start with this config already loaded (no fetch, no race). */
export const __setSiteConfigForTests = (over: Partial<PublicSiteConfig>) => {
  cached = { ...SITE_CONFIG_DEFAULTS, ...over };
  inflight = null;
};
